import type { FeatureKey, PlanFeatures, PlanTier } from '@/types/billing';

export interface PlanFeatureInfo {
  key: FeatureKey;
  label: string;
  description: string;
  requiredTier: PlanTier;
}

export const PLAN_FEATURES_CATALOG: PlanFeatureInfo[] = [
  { key: 'rbac_policies', label: 'RBAC Policies', description: 'Role-based access control policies', requiredTier: 'free' },
  { key: 'abac_policies', label: 'ABAC Policies', description: 'Attribute-based access control with fine-grained conditions', requiredTier: 'team' },
  { key: 'policy_studio', label: 'Policy Studio', description: 'Visual policy editor with drag-and-drop rule building', requiredTier: 'team' },
  { key: 'policy_versioning', label: 'Policy Versioning', description: 'Version history and diff viewer for policies', requiredTier: 'team' },
  { key: 'multi_connector', label: 'Multiple Connectors', description: 'Connect multiple vector databases simultaneously', requiredTier: 'team' },
  { key: 'advanced_analytics', label: 'Advanced Analytics', description: 'Detailed retrieval analytics and trend insights', requiredTier: 'team' },
  { key: 'rebac_policies', label: 'ReBAC Policies', description: 'Relationship-based access control with direct principal-resource bindings', requiredTier: 'team' },
  { key: 'grounded_answers', label: 'Grounded Answers', description: 'Policy-aware answer synthesis with citations', requiredTier: 'team' },
  { key: 'batch_ingestion', label: 'Batch Ingestion', description: 'Batch document and multi-file ingestion endpoints', requiredTier: 'team' },
  { key: 'async_ingestion', label: 'Async Ingestion Jobs', description: 'Queue large ingestion jobs and track progress', requiredTier: 'team' },
  { key: 'source_connectors', label: 'Source Connectors', description: 'Sync documents and their permissions from Google Drive, SharePoint, Confluence, and Notion', requiredTier: 'growth' },
  { key: 'vendor_iam', label: 'Vendor IAM Providers', description: 'Connect Azure Entra ID, AWS IAM, Okta, and more', requiredTier: 'growth' },
  { key: 'access_simulator', label: 'Access Simulator', description: 'Simulate queries to see what a principal can access before going live', requiredTier: 'growth' },
  { key: 'audit_export', label: 'Audit Export', description: 'Export audit logs as CSV or JSON', requiredTier: 'growth' },
  { key: 'sso_scim', label: 'SSO & SCIM', description: 'Enterprise single sign-on and user provisioning', requiredTier: 'growth' },
  { key: 'mcp_server', label: 'MCP Server', description: 'Gateco MCP server for Claude, Cursor, and other AI agents', requiredTier: 'free' },
  { key: 'siem_export', label: 'SIEM export (planned)', description: 'Planned: stream audit events to your SIEM. Until it ships, decision records export as JSON or CSV on Growth and above.', requiredTier: 'enterprise' },
  { key: 'content_ref_mode', label: 'Content Reference Mode', description: 'Return references instead of content for maximum security', requiredTier: 'enterprise' },
  { key: 'custom_kms', label: 'Custom KMS', description: 'Bring your own encryption keys', requiredTier: 'enterprise' },
  { key: 'help_assistant', label: 'Ask AI', description: 'In-product assistant that answers questions about setting up and using Gateco', requiredTier: 'free' },
  { key: 'private_data_plane', label: 'Private Data Plane', description: 'Dedicated infrastructure in your cloud', requiredTier: 'enterprise' },
];

export const PLAN_FEATURES_MAP: Record<PlanTier, PlanFeatures> = {
  free: {
    rbac_policies: true,
    abac_policies: false, policy_studio: false, policy_versioning: false,
    multi_connector: false, advanced_analytics: false, rebac_policies: false, grounded_answers: false,
    batch_ingestion: false, async_ingestion: false, source_connectors: false,
    vendor_iam: false, access_simulator: false, audit_export: false, sso_scim: false, mcp_server: true,
    siem_export: false, content_ref_mode: false, custom_kms: false, private_data_plane: false,
    help_assistant: true,
  },
  team: {
    rbac_policies: true,
    abac_policies: true, policy_studio: true, policy_versioning: true,
    multi_connector: true, advanced_analytics: true, rebac_policies: true, grounded_answers: true,
    batch_ingestion: true, async_ingestion: true, source_connectors: false,
    vendor_iam: false, access_simulator: false, audit_export: false, sso_scim: false, mcp_server: true,
    siem_export: false, content_ref_mode: false, custom_kms: false, private_data_plane: false,
    help_assistant: true,
  },
  growth: {
    rbac_policies: true,
    abac_policies: true, policy_studio: true, policy_versioning: true,
    multi_connector: true, advanced_analytics: true, rebac_policies: true, grounded_answers: true,
    batch_ingestion: true, async_ingestion: true, source_connectors: true,
    vendor_iam: true, access_simulator: true, audit_export: true, sso_scim: true, mcp_server: true,
    siem_export: false, content_ref_mode: false, custom_kms: false, private_data_plane: false,
    help_assistant: true,
  },
  enterprise: {
    rbac_policies: true,
    abac_policies: true, policy_studio: true, policy_versioning: true,
    multi_connector: true, advanced_analytics: true, rebac_policies: true, grounded_answers: true,
    batch_ingestion: true, async_ingestion: true, source_connectors: true,
    vendor_iam: true, access_simulator: true, audit_export: true, sso_scim: true, mcp_server: true,
    siem_export: true, content_ref_mode: true, custom_kms: true, private_data_plane: false,
    help_assistant: true,
  },
};

export const TIER_ORDER: Record<PlanTier, number> = { free: 0, team: 1, growth: 2, enterprise: 3 };

export function getFeatureInfo(key: FeatureKey): PlanFeatureInfo | undefined {
  return PLAN_FEATURES_CATALOG.find((f) => f.key === key);
}

export function getRequiredTier(key: FeatureKey): PlanTier {
  return getFeatureInfo(key)?.requiredTier ?? 'team';
}
